/**
 * Runtime captcha config for the SPA, read from window.__KNOX_CONFIG__ (injected
 * by docker-entrypoint.sh at container start) with Vite build-arg fallbacks.
 *
 * Overseas (IS_CN not set): returns the Google reCAPTCHA score site key.
 * China (IS_CN=true):       returns the Aliyun Captcha 2.0 scene/prefix/region.
 *
 * Centralised here so all captcha-protected pages read from one source and the
 * IS_CN switch lives in exactly one place.
 */
import type { RuntimeCaptchaConfig } from './recaptcha';

type KnoxConfig = {
  IS_CN?: boolean;
  RECAPTCHA_SCORE_SITE_KEY?: string;
  RECAPTCHA_CHECKBOX_SITE_KEY?: string;
  ALIYUN_CAPTCHA_SCENE_ID?: string;
  ALIYUN_CAPTCHA_PREFIX?: string;
  ALIYUN_CAPTCHA_REGION?: string;
};

function config(): KnoxConfig {
  return ((window as unknown as { __KNOX_CONFIG__?: KnoxConfig }).__KNOX_CONFIG__) ?? {};
}

export function isCn(): boolean {
  return config().IS_CN === true;
}

/**
 * Build the RuntimeCaptchaConfig consumed by loadCaptcha()/executeCaptcha().
 * Returns null if captcha isn't configured — callers should then skip the
 * loadCaptcha call and submit without a token (backend will reject if
 * RECAPTCHA_ENABLED=true, matching overseas behaviour).
 */
export function runtimeCaptchaConfig(): RuntimeCaptchaConfig | null {
  const c = config();
  if (c.IS_CN) {
    if (!c.ALIYUN_CAPTCHA_SCENE_ID) return null;
    return {
      aliyun: {
        sceneId: c.ALIYUN_CAPTCHA_SCENE_ID,
        region: c.ALIYUN_CAPTCHA_REGION || 'cn',
        prefix: c.ALIYUN_CAPTCHA_PREFIX || '',
      },
    };
  }
  const scoreSiteKey = c.RECAPTCHA_SCORE_SITE_KEY || import.meta.env.VITE_RECAPTCHA_SCORE_SITE_KEY;
  if (!scoreSiteKey) return null;
  return { scoreSiteKey };
}

/** Google v2 checkbox site key — only used in the overseas NEED_CHALLENGE flow. */
export function checkboxSiteKey(): string | undefined {
  return config().RECAPTCHA_CHECKBOX_SITE_KEY || import.meta.env.VITE_RECAPTCHA_CHECKBOX_SITE_KEY;
}
